import type { FC } from 'react';

import type { Image as CmsImage, Link } from '@eneco/cms-types';
import { AdviceCardImageContainer, Trigger } from '@eneco/custom-components/InspirationAdviceFilter';
import { useTranslation } from '@eneco/i18n';
import { Box, Card, Expandable, Heading, Image, TextLink, Text } from '@eneco/sparky';

import { AdviceCardSkeleton } from './AdviceCard.skeleton';

interface Props {
  heading: string;
  link?: Link;
  image?: CmsImage;
  isLoading?: boolean;
  text?: string;
}

/**
 * The focal point image's `default` and `widescreen` sizes. The Sitecore adapter hands `default` over as `src` and
 * `widescreen` under `sources.xxl`, always with a `srcSet`; a Sanity image has a single size and no `srcSet`, so
 * that size serves both. A Sitecore image without a widescreen size shows no image, as before.
 */
const mapImage = (image: CmsImage | undefined) => ({
  defaultSrc: image?.src,
  widescreenSrc: image?.srcSet === undefined ? image?.src : image.sources?.xxl,
  alt: image?.alt ?? '',
});

export const AdviceCard: FC<Props> = ({ heading, link, image, isLoading, text }) => {
  const { t } = useTranslation();

  if (isLoading) return <AdviceCardSkeleton />;

  const { defaultSrc, widescreenSrc, alt } = mapImage(image);

  return (
    <Card>
      {defaultSrc && widescreenSrc ? (
        <AdviceCardImageContainer borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden" data-testid="card">
          <Image
            alt={alt}
            hasLazyLoad={true}
            height="200px"
            objectFit="cover"
            sources={{
              xl: defaultSrc,
              lg: widescreenSrc,
              md: defaultSrc,
              sm: widescreenSrc,
            }}
            src={defaultSrc}
            width="100%"
          />
        </AdviceCardImageContainer>
      ) : null}
      <Expandable>
        <Box padding="6" paddingBottom="4" paddingTop="5">
          <Trigger label={heading}>
            <Heading as="h2" size="S">
              {heading}
            </Heading>
          </Trigger>
        </Box>
        <Expandable.Content>
          <Box padding="6" paddingTop="0">
            <Text>{text}</Text>
            {link?.href && (
              <>
                <br />
                <TextLink emphasis="high" href={link.href}>
                  {link.text || t('generalLabels.readMore')}
                </TextLink>
              </>
            )}
          </Box>
        </Expandable.Content>
      </Expandable>
    </Card>
  );
};
